if (!customElements.get('feature-cards')) {
  customElements.define('feature-cards', class extends HTMLElement {
    connectedCallback() {
      if (this._controller || !this.hasAttribute('collapse-mobile')) return;
      this._cards = [...this.querySelectorAll('[data-feature-card]')];
      this._button = this.querySelector('[data-feature-toggle]');
      this._grid = this.querySelector('[data-feature-grid]');
      this._viewport = this.querySelector('[data-feature-viewport]');
      if (this._cards.length <= 3 || !this._button || !this._grid || !this._viewport) return;

      this._controller = new AbortController();
      this._desktop = window.matchMedia('(min-width: 1000px)');
      this._motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._fade = this.querySelector('[data-feature-fade]');
      this._expandLabel = this.querySelector('[data-feature-expand-label]');
      this._collapseLabel = this.querySelector('[data-feature-collapse-label]');
      this._expanded = false;
      const options = { signal: this._controller.signal };

      this._button.addEventListener('click', () => {
        this._expanded = !this._expanded;
        this._update({ animate: true, keepButtonVisible: !this._expanded });
      }, options);
      this._desktop.addEventListener('change', () => this._update(), options);
      this._motion.addEventListener('change', () => this._update(), options);
      this._viewport.addEventListener('transitionend', (event) => {
        if (event.target === this._viewport && event.propertyName === 'height') this._finishToggle();
      }, options);
      this.addEventListener('shopify:block:select', (event) => {
        const index = this._cards.indexOf(event.target.closest('[data-feature-card]'));
        if (index >= 3) {
          this._expanded = true;
          this._update();
        }
      }, options);

      if ('ResizeObserver' in window) {
        this._observer = new ResizeObserver(() => this._update());
        this._observer.observe(this._grid);
      } else {
        window.addEventListener('resize', () => this._update(), options);
      }
      document.fonts?.ready.then(() => {
        if (this._controller) this._update();
      });
      this._update();
    }

    _update({ animate = false, keepButtonVisible = false } = {}) {
      const collapsed = !this._desktop.matches && !this._expanded;
      const fullHeight = this._grid.getBoundingClientRect().height;
      let height = fullHeight;
      if (collapsed) {
        const preview = this._cards[3].getBoundingClientRect();
        height = preview.top - this._grid.getBoundingClientRect().top + Math.min(59, preview.height);
      }
      // Follow yg-dropdown's measured-height CSS transition, using Tailwind utilities.
      // Numeric heights also allow the browser to reverse an interrupted transition.
      const shouldAnimate = animate && !this._desktop.matches && !this._motion.matches;
      this._keepButtonVisible = keepButtonVisible;
      this._viewport.style.transitionDuration = shouldAnimate ? '' : '0s';
      this._viewport.style.height = this._desktop.matches ? '' : `${height}px`;
      this.toggleAttribute('data-collapsed', collapsed);
      this._button.hidden = this._desktop.matches;
      this._button.setAttribute('aria-expanded', String(!collapsed));
      this._expandLabel.hidden = !collapsed;
      this._collapseLabel.hidden = collapsed;
      this._fade.hidden = !collapsed;
      this._cards.slice(3).forEach((card) => {
        card.inert = collapsed;
        if (collapsed) card.setAttribute('aria-hidden', 'true');
        else card.removeAttribute('aria-hidden');
      });

      if (!shouldAnimate || Math.abs(this._viewport.getBoundingClientRect().height - height) < 1) {
        this._finishToggle();
      }
    }

    _finishToggle() {
      // A long collapsed list can move its focused button above the viewport.
      if (this._keepButtonVisible && this._button.getBoundingClientRect().top < 0) {
        this._button.scrollIntoView({
          block: 'nearest',
          behavior: this._motion.matches ? 'instant' : 'smooth',
        });
      }
      this._keepButtonVisible = false;
    }

    disconnectedCallback() {
      this._controller?.abort();
      this._observer?.disconnect();
      if (this._viewport) {
        this._viewport.style.removeProperty('height');
        this._viewport.style.removeProperty('transition-duration');
      }
      this._controller = null;
      this.removeAttribute('data-collapsed');
      if (this._button) this._button.hidden = true;
      if (this._fade) this._fade.hidden = true;
      this._cards?.forEach((card) => {
        card.inert = false;
        card.removeAttribute('aria-hidden');
      });
    }
  });
}
